import { ErrorMessage } from '@hookform/error-message'
import { browserLocalPersistence, setPersistence, signInWithEmailAndPassword } from 'firebase/auth'
import { FC } from 'react'
import { Col, Container, Row } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { useSetRecoilState } from 'recoil'
import { auth, handleLoginError } from '../../../firebase'
import { ResendEmailPath, SendEmailPath } from '../../../routes/path'
import { requestState } from '../../../states/atoms'
import { StyleFormHead } from '../../../styles/mi'
import { Button } from '../../Button'
import { HelperText } from '../../HelperText'
import { Input } from '../Input'

type Account = {
  email: string
  password: string
}

export const LoginSection: FC = () => {
  const setRequestState = useSetRecoilState(requestState)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Account>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: { email: '', password: '' },
  })

  const onSubmit = async (data: Account) => {
    setRequestState({ isLoading: true })
    const { email, password } = data
    try {
      await setPersistence(auth, browserLocalPersistence)
      await signInWithEmailAndPassword(auth, email, password)
      setRequestState({ isLoading: false })
    } catch (error) {
      let errorMessage = `認証に失敗しました。\n時間を置いて再度お試しください。`
      if (typeof error === 'object' && handleLoginError(JSON.stringify(error)) === 'auth/too-many-requests') {
        errorMessage = `認証が一時的にロックされました。\n時間を置いて再度お試しください。`
      }

      setRequestState({
        isLoading: false,
        errorMessage: errorMessage,
      })
    }
  }

  return (
    <>
      <div className="containerBase">
        <Container>
          <form onSubmit={handleSubmit(onSubmit)} className="miForm">
            <Row>
              <StyleFormHead>
                <img src="/images/miproteger-logo.png" alt="" />
                <p>ログイン</p>
              </StyleFormHead>
            </Row>
            <Row>
              <hr className="solid"></hr>
            </Row>

            <Row>
              <Col className="inputBox">
                <ErrorMessage
                  errors={errors}
                  name="email"
                  render={({ message }) => <HelperText>*{message}</HelperText>}
                />
                <Input
                  type="mail"
                  placeholder="メールアドレス"
                  isError={!!errors.email}
                  {...register('email', {
                    required: 'メールアドレスは必須です',
                    pattern: {
                      value: /^[a-zA-Z0-9_+-]+(.[a-zA-Z0-9_+-]+)*@([a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9]*\.)+[a-zA-Z]{2,}$/i,
                      message: '正しい形式で入力してください',
                    },
                  })}
                />
              </Col>
            </Row>

            <Row>
              <Col className="inputBox">
                <div>
                  <ErrorMessage
                    errors={errors}
                    name="password"
                    render={({ message }) => <HelperText>*{message}</HelperText>}
                  />
                </div>
                <Input
                  type="password"
                  placeholder="パスワード"
                  isError={!!errors.password}
                  {...register('password', {
                    required: 'パスワードは必須です',
                    pattern: {
                      value: /^[!-~]{8,16}$/,
                      message: '8~16文字で入力してください',
                    },
                  })}
                />
              </Col>
            </Row>
            <Row>
              <Col className="link-box">
                <div className="link">
                  <a href={SendEmailPath}>初めての方はこちら</a>
                </div>
                <div className="link">
                  <a href={ResendEmailPath}>パスワードをお忘れですか?</a>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className="button">
                <Button
                  buttonType="contained"
                  isSubmit
                  onClick={() => undefined}
                  disabled={!!Object.keys(errors).length}
                  isFullWidth
                >
                  ログイン
                </Button>
              </Col>
            </Row>
          </form>
        </Container>
      </div>
    </>
  )
}
